import React, { useState, useEffect } from "react";
import { 
  BanknotesIcon, 
  ClockIcon, 
  UserCircleIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  TicketIcon,
  PlusIcon,
  ArrowRightIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon as ExternalLinkIcon
} from "@heroicons/react/24/solid";
import { useNavigate } from 'react-router-dom';
import api, { setToken } from "../api";
import Modal from "../components/Modal";

export default function P2P() {
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);
  const [userData, setUserData] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [formData, setFormData] = useState({
    chipAmount: '',
    arenaRate: '',
    walletAddress: '',
    paymentMethods: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Transaction details states
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [copied, setCopied] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  
  // New states for transaction status checking
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [txHash, setTxHash] = useState(null);
  
  const navigate = useNavigate();
  
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) setToken(token);
    
    const fetchData = async () => {
      try {
        // Fetch user data
        const profileResponse = await api.get("/profile");
        setUserData(profileResponse.data);
        
        // Fetch P2P advertisements
        const adsResponse = await api.get(`/p2p/advertisements?page=${page}&limit=10`);
        setAds(adsResponse.data.advertisements);
        setTotalPages(adsResponse.data.pagination.pages);
      } catch (err) {
        console.error("Failed to load data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [page]);
  
  // Function to check transaction status
// Update the checkTransactionStatus function in P2P.js
const checkTransactionStatus = async () => {
  if (!transactionDetails || !transactionDetails._id) {
    setError("Transaction details not available");
    return;
  }
  
  setIsCheckingStatus(true);
  
  try {
    // Call API to check transaction status
    const response = await api.get(`/p2p/transaction/${transactionDetails._id}`);
    
    // Update transaction status
    setTransactionStatus(response.data.transaction);
    
    // Show message if transaction was detected and completed during this check
    if (response.data.blockchainCheck?.updated) {
      setSuccess('Your payment has been detected and chips have been credited to your account!');
      
      // Also update user data to show new chip balance
      const profileResponse = await api.get("/profile");
      setUserData(profileResponse.data);
    }
    
    // If transaction has a txHash, store it for the explorer link
    if (response.data.transaction.txHash) {
      setTxHash(response.data.transaction.txHash);
    }
  } catch (err) {
    console.error('Error checking transaction status:', err);
    setError('Failed to check transaction status');
  } finally {
    setIsCheckingStatus(false);
  }
};
  
  const formatTime = date => {
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    }
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };
  
  const handleCreateAd = async () => {
    setError('');
    setSuccess('');
    
    // Validate form
    if (!formData.chipAmount || !formData.arenaRate || !formData.walletAddress || formData.paymentMethods.length === 0) {
      setError('Please fill all required fields');
      return;
    }
    
    // Validate chip amount
    if (parseInt(formData.chipAmount) > userData.chips) {
      setError(`You only have ${userData.chips} chips available`);
      return;
    }
    
    try {
      const response = await api.post('/p2p/create-ad', {
        chipAmount: parseInt(formData.chipAmount),
        arenaRate: parseFloat(formData.arenaRate),
        sellerWalletAddress: formData.walletAddress,
        paymentMethods: formData.paymentMethods
      });
      
      setSuccess('Advertisement created successfully!');
      setUserData({...userData, chips: response.data.remainingChips});
      
      // Reset form and close modal after short delay
      setTimeout(() => {
        setFormData({
          chipAmount: '',
          arenaRate: '',
          walletAddress: '',
          paymentMethods: []
        });
        setCreateModalOpen(false);
        
        // Refresh ads
        api.get(`/p2p/advertisements?page=1&limit=10`)
          .then(res => {
            setAds(res.data.advertisements);
            setPage(1);
            setTotalPages(res.data.pagination.pages);
          });
      }, 2000);
    } catch (err) {
      console.error('Error creating ad:', err);
      setError(err.response?.data?.message || 'Failed to create advertisement');
    }
  };
  
  const handleBuy = ad => {
    setSelectedAd(ad);
    setBuyModalOpen(true);
  };
  
  const confirmBuy = async paymentMethod => {
    setError('');
    setSuccess('');
    
    try {
      const response = await api.post('/p2p/initiate-transaction', {
        advertisementId: selectedAd._id,
        paymentMethod
      });
      
      // Reset any previous transaction status
      setTransactionStatus(null);
      setTxHash(null);
      
      // Store transaction details
      setTransactionDetails(response.data.transaction);
      setWalletAddress(response.data.escrowWalletAddress);
      setPaymentInstructions(response.data.paymentInstructions);
      
      // Show transaction details instead of redirecting
      setShowTransactionDetails(true);
      setBuyModalOpen(false);
      
      // No timeout or redirection - let the user see the address
    } catch (err) {
      console.error('Error initiating transaction:', err);
      setError(err.response?.data?.message || 'Failed to initiate transaction');
    }
  };
  
  const togglePaymentMethod = method => {
    if (formData.paymentMethods.includes(method)) {
      setFormData({
        ...formData,
        paymentMethods: formData.paymentMethods.filter(m => m !== method)
      });
    } else {
      setFormData({
        ...formData,
        paymentMethods: [...formData.paymentMethods, method]
      });
    }
  };

  // Function to open Solana Explorer
  const openExplorer = () => {
    // If we have a transaction hash, include it in the URL
    const explorerBaseUrl = "https://explorer.solana.com/tx";
    const url = txHash ? `${explorerBaseUrl}/${txHash}` : "https://explorer.solana.com";
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse" />
            <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin" />
          </div>
          <div className="mt-4 font-mono text-sm">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Loading P2P Market…
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-12 px-4 text-white font-mono">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-8">
          P2P Market
        </h1>

        {/* Market Actions */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
          <div className="text-sm text-gray-300 flex items-center">
            <BanknotesIcon className="h-5 w-5 text-pink-400 mr-2" />
            <span>Current Exchange: <span className="text-pink-400 font-medium">1 CHIP = 1 ARENA</span></span>
          </div>

          <button 
            onClick={() => setCreateModalOpen(true)}
            className="px-6 py-2 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium transition-colors flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create Ad</span>
          </button>
        </div>

        {/* Transaction Details - UPDATED WITH STATUS CHECKING */}
        {showTransactionDetails && (
          <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
                  Transaction Initiated
                </h2>
                <div className="h-px flex-grow ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />
              </div>
              
              <div className="p-4 bg-black bg-opacity-30 rounded-lg border border-gray-700 mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3">
                  <div className="text-sm text-gray-300 mb-2 md:mb-0">You are purchasing:</div>
                  <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                    {transactionDetails?.chipAmount.toLocaleString()} CHIPS
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3">
                  <div className="text-sm text-gray-300 mb-2 md:mb-0">Total payment:</div>
                  <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                    {transactionDetails?.arenaAmount.toLocaleString()} ARENA
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div className="text-sm text-gray-300 mb-2 md:mb-0">Payment method:</div>
                  <div className="px-3 py-1 bg-gradient-to-r from-violet-500/20 to-pink-500/20 rounded-md border border-pink-500/30 text-pink-400">
                    {transactionDetails?.paymentMethod}
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="text-sm text-gray-300 mb-2">Please send ARENA to this wallet address:</div>
                <div className="flex flex-col">
                  <div className="p-3 bg-black bg-opacity-50 rounded-lg border border-gray-700 font-mono text-sm mb-2 break-all">
                    {walletAddress}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(walletAddress);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="self-end px-3 py-1 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium text-sm transition-colors flex items-center space-x-1"
                  >
                    {copied ? (
                      <>
                        <CheckIcon className="h-4 w-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        <span>Copy Address</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Transaction Status Section - NEW */}
              <div className="mb-6 p-4 border border-gray-700 rounded-lg bg-black bg-opacity-40">
                <div className="flex justify-between items-center mb-3">
                  <div className="text-sm text-gray-300">Transaction Status:</div>
                  <div className={`px-3 py-1 rounded-md ${
                    !transactionStatus ? 'bg-gray-700/30 text-gray-400' :
                    transactionStatus.status === 'pending' ? 'bg-yellow-700/30 border border-yellow-500/30 text-yellow-400' :
                    transactionStatus.status === 'payment_detected' ? 'bg-blue-700/30 border border-blue-500/30 text-blue-400' :
                    transactionStatus.status === 'completed' ? 'bg-green-700/30 border border-green-500/30 text-green-400' :
                    'bg-red-700/30 border border-red-500/30 text-red-400'
                  }`}>
                    {!transactionStatus ? 'Unknown' :
                     transactionStatus.status === 'pending' ? 'Pending Payment' :
                     transactionStatus.status === 'payment_detected' ? 'Payment Detected' :
                     transactionStatus.status === 'completed' ? 'Completed' :
                     transactionStatus.status}
                  </div>
                </div>
                
                {transactionStatus && transactionStatus.status === 'completed' && (
                  <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-md text-green-300 text-sm mb-3">
                    <div className="flex items-center mb-1">
                      <CheckIcon className="h-4 w-4 mr-1" />
                      <span className="font-bold">Transaction Complete!</span>
                    </div>
                    <p>Your account has been credited with {transactionStatus.chipAmount.toLocaleString()} CHIPS.</p>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">
                    {transactionStatus ? 
                      `Last checked: ${new Date().toLocaleString()}` : 
                      'Check transaction status after sending ARENA'}
                  </span>
                  <button
                    onClick={checkTransactionStatus}
                    disabled={isCheckingStatus}
                    className="px-3 py-1 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium text-sm transition-colors flex items-center space-x-1"
                  >
                    {isCheckingStatus ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white rounded-full border-t-transparent animate-spin mr-1"></div>
                        <span>Checking...</span>
                      </>
                    ) : (
                      <>
                        <span>Check Status</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="p-4 bg-amber-900/30 border border-amber-500/50 text-amber-300 rounded-lg mb-6 text-sm">
                <div className="flex items-center mb-2">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span className="font-bold">Important</span>
                </div>
                <p>Once you send the ARENA tokens, the transaction will be processed automatically. This may take a few minutes. Use the "Check Status" button to verify if your payment has been detected.</p>
              </div>
              
              <div className="flex flex-col md:flex-row justify-between gap-4">
                {/* Updated buttons based on transaction status */}
                {transactionStatus && transactionStatus.status === 'completed' ? (
                  <button
                    onClick={() => {
                      setShowTransactionDetails(false);
                      // Refresh the ad listings
                      api.get(`/p2p/advertisements?page=1&limit=10`)
                        .then(res => {
                          setAds(res.data.advertisements);
                          setPage(1);
                          setTotalPages(res.data.pagination.pages);
                        });
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>Continue Shopping</span>
                  </button>
                ) : (
                  <button
                    onClick={openExplorer}
                    className="px-6 py-3 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>Check Blockchain Explorer</span>
                    <ExternalLinkIcon className="h-4 w-4" />
                  </button>
                )}
                
                <button
                  onClick={() => setShowTransactionDetails(false)}
                  className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 rounded-md border border-gray-700 hover:border-gray-600 text-gray-300 font-medium transition-colors"
                >
                  Back to Marketplace
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
                Available Offers
              </h2>
              <div className="h-px flex-grow ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />
            </div>

            {/* Ad List */}
            <div className="space-y-4">
              {ads.length > 0 ? (
                ads.map(ad => (
                  <div 
                    key={ad._id} 
                    className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 hover:border-pink-500 transition-duration-300"
                  >
                    <div className="p-4 bg-black bg-opacity-60 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center border border-gray-700">
                            <UserCircleIcon className="h-6 w-6 text-pink-400" />
                          </div>
                          <div>
                            <div className="text-sm text-white">{ad.seller.email.split('@')[0]}</div>
                            <div className="flex items-center text-xs text-gray-400">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              {formatTime(ad.createdAt)}
                            </div>
                          </div>
                        </div>

                        <div className="px-3 py-1 bg-gradient-to-r from-violet-500/20 to-pink-500/20 rounded border border-pink-500/30 text-pink-400 text-sm flex items-center">
                          <ShieldCheckIcon className="h-4 w-4 mr-1" />
                          Verified Seller
                        </div>
                      </div>

                      <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between">
                        <div className="flex items-center gap-3 mb-3 sm:mb-0">
                          <div className="bg-black bg-opacity-70 px-3 py-2 rounded-lg border border-gray-700">
                            <div className="text-xs text-gray-400 mb-1">Amount</div>
                            <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                              {ad.chipAmount.toLocaleString()} <span className="text-sm">CHIPS</span>
                            </div>
                          </div>

                          <div className="bg-black bg-opacity-70 px-3 py-2 rounded-lg border border-gray-700">
                            <div className="text-xs text-gray-400 mb-1">Rate</div>
                            <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                              {ad.arenaRate.toFixed(2)} <span className="text-sm">ARENA</span>
                            </div>
                          </div>
                        </div>

                        <button 
                          onClick={() => handleBuy(ad)}
                          className="px-6 py-2 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium transition-colors"
                        >
                          Buy Now
                        </button>
                      </div>

                      <div className="mt-3 text-xs text-gray-400">
                        Payment Methods: {ad.paymentMethods.join(", ")}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400">
                  No active advertisements found
                </div>
              )}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-8 h-8 rounded-md flex items-center justify-center ${
                      page === i + 1
                        ? 'bg-gradient-to-r from-violet-500 to-pink-500 text-white'
                        : 'bg-black bg-opacity-60 border border-gray-700 text-gray-300'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* How It Works */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
                How It Works
              </h2>
              <div className="h-px flex-grow ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-black bg-opacity-60 rounded-lg border border-gray-700">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 mb-3">
                  <span className="text-white font-bold">1</span>
                </div>
                <h3 className="text-lg font-bold text-pink-400 mb-2">Create an Ad</h3>
                <p className="text-sm text-gray-300">Set your price and payment methods for selling CHIPS. Your chips will be held in escrow until the trade is complete.</p>
              </div>

              <div className="p-4 bg-black bg-opacity-60 rounded-lg border border-gray-700">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 mb-3">
                  <span className="text-white font-bold">2</span>
                </div>
                <h3 className="text-lg font-bold text-pink-400 mb-2">Secure Trade</h3>
                <p className="text-sm text-gray-300">Buyer sends ARENA tokens to our escrow wallet. The blockchain transaction is automatically verified.</p>
              </div>

              <div className="p-4 bg-black bg-opacity-60 rounded-lg border border-gray-700">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 mb-3">
                  <span className="text-white font-bold">3</span>
                </div>
                <h3 className="text-lg font-bold text-pink-400 mb-2">Automatic Settlement</h3>
                <p className="text-sm text-gray-300">Once payment is confirmed, chips are transferred to the buyer and ARENA tokens are sent to the seller's wallet.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl">
          <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <TicketIcon className="h-8 w-8 text-pink-400 mr-3" />
              <div>
                <h3 className="text-lg font-bold text-white">Got any problem?</h3>
                <p className="text-sm text-gray-400">Our support team is here to help 24/7</p>
              </div>
            </div>
            <button className="px-6 py-3 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white font-medium transition-colors flex items-center space-x-2">
              <span>Raise a Ticket</span>
            </button>
          </div>
        </div>

{/* Create Ad Modal */}
{createModalOpen && (
          <Modal onClose={() => setCreateModalOpen(false)}>
            <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-lg border border-gray-700 max-w-md w-full">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 mb-4">
                Create P2P Advertisement
              </h2>
              
              {error && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-md text-red-300 text-sm">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="mb-4 p-3 bg-green-900/30 border border-green-500/50 rounded-md text-green-300 text-sm">
                  {success}
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-1">Chip Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.chipAmount}
                    onChange={(e) => setFormData({...formData, chipAmount: e.target.value})}
                    className="w-full bg-black bg-opacity-70 border border-gray-700 rounded-md px-3 py-2 text-white focus:border-pink-500 focus:outline-none"
                    placeholder="Amount of chips to sell"
                  />
                  <div className="absolute right-3 top-2 text-gray-400 text-sm">
                    CHIPS
                  </div>
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  Available: {userData?.chips.toLocaleString()} CHIPS
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-1">ARENA Rate</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.arenaRate}
                    onChange={(e) => setFormData({...formData, arenaRate: e.target.value})}
                    className="w-full bg-black bg-opacity-70 border border-gray-700 rounded-md px-3 py-2 text-white focus:border-pink-500 focus:outline-none"
                    placeholder="Rate per chip (e.g. 1.00)"
                  />
                  <div className="absolute right-3 top-2 text-gray-400 text-sm">
                    ARENA
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-1">Your Wallet Address</label>
                <input
                  type="text"
                  value={formData.walletAddress}
                  onChange={(e) => setFormData({...formData, walletAddress: e.target.value})}
                  className="w-full bg-black bg-opacity-70 border border-gray-700 rounded-md px-3 py-2 text-white focus:border-pink-500 focus:outline-none"
                  placeholder="Solana wallet address to receive ARENA"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-2">Payment Methods</label>
                <div className="space-y-2">
                  {['Bank Transfer', 'PayPal', 'Crypto', 'Other'].map(method => (
                    <div key={method} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`method-${method}`}
                        checked={formData.paymentMethods.includes(method)}
                        onChange={() => togglePaymentMethod(method)}
                        className="mr-2 form-checkbox h-4 w-4 text-pink-500 border-gray-700 rounded focus:ring-0"
                      />
                      <label htmlFor={`method-${method}`} className="text-gray-300 text-sm">
                        {method}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setCreateModalOpen(false)}
                  className="px-4 py-2 bg-gray-800 rounded-md text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAd}
                  className="px-4 py-2 bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 rounded-md text-white transition-colors"
                >
                  Create Ad
                </button>
              </div>
            </div>
          </Modal>
        )}
        
        {/* Buy Modal */}
        {buyModalOpen && selectedAd && (
          <Modal onClose={() => setBuyModalOpen(false)}>
            <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-lg border border-gray-700 max-w-md w-full">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 mb-4">
                Buy Chips
              </h2>
              
              {error && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-md text-red-300 text-sm">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="mb-4 p-3 bg-green-900/30 border border-green-500/50 rounded-md text-green-300 text-sm">
                  {success}
                </div>
              )}
              
              <div className="mb-4 p-4 border border-gray-700 rounded-lg bg-black bg-opacity-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm">You will receive:</span>
                  <span className="text-lg font-bold text-pink-400">
                    {selectedAd.chipAmount.toLocaleString()} CHIPS
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">You will pay:</span>
                  <span className="text-lg font-bold text-violet-400">
                    {(selectedAd.chipAmount * selectedAd.arenaRate).toLocaleString()} ARENA
                  </span>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="text-gray-400 text-sm">Rate: 1 CHIP = {selectedAd.arenaRate.toFixed(2)} ARENA</div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-2">Select Payment Method</label>
                <div className="space-y-2">
                  {selectedAd.paymentMethods.map(method => (
                    <button
                      key={method}
                      onClick={() => confirmBuy(method)}
                      className="w-full flex justify-between items-center p-3 bg-black bg-opacity-70 border border-gray-700 rounded-md text-white hover:border-pink-500 transition-colors"
                    >
                      <span>{method}</span>
                      <ArrowRightIcon className="h-4 w-4 text-pink-400" />
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mt-2 text-xs text-gray-400">
                By proceeding, you agree to send {(selectedAd.chipAmount * selectedAd.arenaRate).toLocaleString()} ARENA to our escrow wallet. Once payment is confirmed, the chips will be automatically transferred to your account.
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setBuyModalOpen(false)}
                  className="px-4 py-2 bg-gray-800 rounded-md text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </main>
  );
}
